/* eslint-disable react/jsx-no-bind */
import { ArrowBack, ContentCopy } from '@mui/icons-material';
import {
  Box,
  Typography,
  CardContent,
  Card,
  Tabs,
  Tab,
  Button,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';
import bookingService from '../Booking/bookingService';

const BookingDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { booking: stateBooking } = location.state || {};
  const [booking, setBooking] = useState(stateBooking || null);
  const [isLoading, setIsLoading] = useState(!stateBooking);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (bookingId) {
        setIsLoading(true);
        try {
          const bookingData = await bookingService.getBookingDetails(bookingId);
          setBooking(bookingData);
        } catch (error) {
          logger.error('Error fetching booking details:', error);
          // toast.error("Failed to load booking details");
          // Fall back to state booking if fetch fails
          if (stateBooking) {
            setBooking(stateBooking);
          }
        } finally {
          setIsLoading(false);
        }
      } else if (stateBooking) {
        setBooking(stateBooking);
        setIsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId, stateBooking]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${type} copied to clipboard!`);
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  };

  const generateTransactionId = () => {
    return `TXN${Date.now()}${Math.floor(Math.random() * 1000)}`;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!booking) {
    return (
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box sx={{ padding: 4 }}>
            <Typography>No Booking selected</Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/booking')}
            >
              Back to Bookings
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Horizontal Timeline Component
  const TimeLine = ({ data, status }) => {
    const allSteps = [
      { id: 'created', label: 'Created', code: 'CRT' },
      { id: 'assigned', label: 'Assigned', code: 'ASG' },
      { id: 'pickup', label: 'Pickup', code: 'RFP' },
      { id: 'transit', label: 'In Transit', code: 'INT' },
      { id: 'delivered', label: 'Delivered', code: 'DLD' },
    ];

    const incidentSteps = [
      { id: 'created', label: 'Created', code: 'CRT' },
      { id: 'assigned', label: 'Assigned', code: 'ASG' },
      { id: 'pickup', label: 'Pickup', code: 'RFP' },
      { id: 'transit', label: 'In Transit', code: 'INT' },
      { id: 'incident', label: 'Incident Reported', code: 'ICR' },
    ];

    const cancelledSteps = [
      { id: 'created', label: 'Created', code: 'CRT' },
      { id: 'assigned', label: 'Assigned', code: 'ASG' },
      { id: 'cancelled', label: 'Canceled', code: 'CLD' },
    ];

    const readyForPickupSteps = [
      { id: 'created', label: 'Created', code: 'CRT' },
      { id: 'assigned', label: 'Assigned', code: 'ASG' },
      { id: 'ready', label: 'Ready for Pickup', code: 'RFP' },
    ];

    const notifySteps = [
      { id: 'created', label: 'Created', code: 'CRT' },
      { id: 'assigned', label: 'Assigned', code: 'ASG' },
      { id: 'notify', label: 'Notify', code: 'NTY' },
    ];

    const getSteps = () => {
      if (status === 'Canceled') return cancelledSteps;
      if (status === 'Ready for Pickup') return readyForPickupSteps;
      if (status === 'Notify') return notifySteps;
      if (status === 'Incident Reported') return incidentSteps;
      return allSteps;
    };

    const steps = getSteps();

    const getActiveSteps = () => {
      switch (status) {
        case 'Created':
          return ['created'];
        case 'Assigned':
          return ['created', 'assigned'];
        case 'Pickup':
          return ['created', 'assigned', 'pickup'];
        case 'In Transit':
          return ['created', 'assigned', 'pickup', 'transit'];
        case 'Delivered':
          return ['created', 'assigned', 'pickup', 'transit', 'delivered'];
        case 'Incident Reported':
          return ['created', 'assigned', 'pickup', 'transit', 'incident'];
        case 'Canceled':
          return ['created', 'assigned', 'cancelled'];
        case 'Ready for Pickup':
          return ['created', 'assigned', 'ready'];
        case 'Notify':
          return ['created', 'assigned', 'notify'];
        default:
          return [];
      }
    };

    const activeSteps = getActiveSteps();
    const cancelled = status === 'Canceled';
    const incident = status === 'Incident Reported';

    return (
      <Box
        sx={{ position: 'relative', padding: '40px 20px', overflowX: 'auto' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            minWidth: '600px',
          }}
        >
          {steps.map((step, index) => {
            const isActive = activeSteps.includes(step.id);
            const hasData = data?.find((item) => item.status === step.code);

            return (
              <Box
                key={step.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  minWidth:
                    cancelled && step.id === 'cancelled'
                      ? '150px'
                      : incident && step.id === 'incident'
                        ? '150px'
                        : step.id === 'delivered'
                          ? '150px'
                          : '120px',
                }}
              >
                {/* Connector line */}
                {index > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '12px',
                      left:
                        cancelled && steps[index - 1].id === 'assigned'
                          ? '-75px'
                          : incident && steps[index - 1].id === 'transit'
                            ? '-75px'
                            : steps[index - 1].id === 'transit'
                              ? '-75px'
                              : '-60px',
                      width:
                        cancelled && steps[index - 1].id === 'assigned'
                          ? '75px'
                          : incident && steps[index - 1].id === 'transit'
                            ? '75px'
                            : steps[index - 1].id === 'transit'
                              ? '75px'
                              : '60px',
                      height: '4px',
                      backgroundColor: isActive
                        ? cancelled
                          ? '#dc3545'
                          : incident
                            ? '#ff6b6b'
                            : '#00c996'
                        : '#e0e0e0',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Circle indicator */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.2 }}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: isActive
                      ? cancelled
                        ? '#dc3545'
                        : incident
                          ? '#ff6b6b'
                          : '#00c996'
                      : '#e0e0e0',
                    border: isActive ? '3px solid white' : '3px solid #f5f5f5',
                    marginBottom: '8px',
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.2 + 0.1 }}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                      }}
                    />
                  )}
                </motion.div>

                {/* Label and timestamp */}
                <Box sx={{ textAlign: 'center' }}>
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 'bold' : 'normal',
                        color: isActive
                          ? cancelled
                            ? '#dc3545'
                            : incident
                              ? '#ff6b6b'
                              : '#00c996'
                          : 'text.primary',
                      }}
                    >
                      {step.label}
                    </Typography>
                    {hasData && hasData.createdOn && (() => {
                      const formattedDate = formatToIST(hasData.createdOn);
                      const spaceIndex = formattedDate.indexOf(' ');
                      const datePart = formattedDate.substring(0, spaceIndex);
                      const timePart = formattedDate.substring(spaceIndex + 1);
                      return (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              display: 'block',
                              lineHeight: 1.2,
                            }}
                          >
                            {datePart}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              display: 'block',
                              lineHeight: 1.2,
                            }}
                          >
                            {timePart}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </motion.div>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'in transit':
        return 'warning';
      case 'created':
        return 'info';
      case 'canceled':
        return 'error';
      case 'ready for pickup':
        return 'secondary';
      case 'assigned':
        return 'primary';
      case 'pickup':
        return 'info';
      case 'incident reported':
        return 'error';
      case 'notify':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 2, pl: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 3,
          mb: 3,
          bgcolor: '#00c996',
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <IconButton
          onClick={() => navigate('/booking')}
          sx={{
            width: 50,
            height: 50,
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
          Booking Details
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ boxShadow: 2, borderRadius: 2, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#00c996',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              '&.Mui-selected': {
                color: 'white',
                fontWeight: 600,
                bgcolor: '#00c996',
                borderRadius: '8px 8px 0 0',
              },
            },
          }}
        >
          <Tab label="Booking Info" />
          <Tab label="Sender Details" />
          <Tab label="Traveler Details" />
          <Tab label="Track Package" />
          <Tab label="Fare Details" />
        </Tabs>
      </Card>

      {/* Content */}
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          {/* Booking Info */}
          {activeTab === 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#00c996',
                }}
              >
                Booking Information
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                  },
                  gap: 3,
                  mt: 2,
                }}
              >
                <GridItem
                  label="Booking Id"
                  value={booking?.bookingId || 'N/A'}
                />
                <GridItem
                  label="Status"
                  value={
                    <Chip
                      label={booking?.status || 'N/A'}
                      color={getStatusColor(booking?.status)}
                      size="small"
                    />
                  }
                />
                <GridItem
                  label="Booking Date"
                  value={
                    booking?.bookingDateTime
                      ? formatToIST(booking.bookingDateTime)
                      : 'N/A'
                  }
                />
                <GridItem label="Source" value={booking?.source || 'N/A'} />
                <GridItem
                  label="Destination"
                  value={booking?.destination || 'N/A'}
                />
                <GridItem
                  label="End Date"
                  value={
                    booking?.endDate ? formatToIST(booking.endDate) : 'N/A'
                  }
                />
                <GridItem
                  label="Package Type"
                  value={booking?.packageType || 'N/A'}
                />
                <GridItem
                  label="Package Size/Weight"
                  value={booking?.packageSizeWeight || 'N/A'}
                />
                {/*  <GridItem
                  label="Special Instructions"
                  value={booking?.specialInstructions || 'N/A'}
                />*/}
                <GridItem
                  label="Amount"
                  value={`₹${booking?.amount || 'N/A'}`}
                />
                {/* <GridItem label="Tracking Id" value={booking.trackingId} />
                <GridItem label="offer Code" value={booking.offerCode} />
                <GridItem label="Offer Amount" value={booking.offerAmount} />*/}
              </Box>
            </Box>
          )}

          {/* Sender Details */}
          {activeTab === 1 && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#00c996',
                }}
              >
                Sender Details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                  },
                  gap: 3,
                }}
              >
                <GridItem
                  label="User Id"
                  value={booking?.senderDetails?.userId || 'N/A'}
                />

                <GridItem
                  label="Sender Name"
                  value={booking?.senderDetails?.name || 'N/A'}
                />
                <GridItem
                  label="Phone Number"
                  value={booking?.senderDetails?.phone || 'N/A'}
                />
                <GridItem
                  label="Email"
                  value={booking?.senderDetails?.email || 'N/A'}
                />
                <GridItem
                  label="Source"
                  value={booking?.senderDetails?.source || 'N/A'}
                />
                <GridItem
                  label="Destination"
                  value={booking?.senderDetails?.destination || 'N/A'}
                />
                <GridItem
                  label="Excepted Delivery"
                  value={booking?.senderDetails?.exceptedDelivery ? formatToIST(booking?.senderDetails?.exceptedDelivery) : 'N/A'}
                />
                <GridItem
                  label="Created On"
                  value={booking?.senderDetails?.createdOn ? formatToIST(booking?.senderDetails?.createdOn) : 'N/A'}
                />
                <GridItem
                  label="Updated On"
                  value={booking?.senderDetails?.updatedOn || 'N/A'}
                />
{booking?.status === 'Delivered' && (
                  <>
                    <GridItem
                      label="Rating"
                      value={
                        booking?.senderDetails?.rating != null && Number(booking.senderDetails.rating) > 0
                          ? `${booking.senderDetails.rating}⭐`
                          : 'Not Rated'
                      }
                    />
                    <GridItem
                      label="Review"
                      value={
                        booking?.senderDetails?.review && String(booking.senderDetails.review).trim()
                          ? booking.senderDetails.review
                          : 'Not Rated'
                      }
                    />
                  </>
                )}
                {/* <GridItem
                  label="PickUp Address"
                  value={booking.senderDetails?.pickupAddress}
                />
                <GridItem
                  label="Payment Method"
                  value={booking.senderDetails?.paymentMethod}
                />>*/}
              {/*  <GridItem
                  label="Rating"
                  value={
                    <Box>
                      {(() => {
                        const travelerRating = booking?.ratings?.find(
                          (r) => r.ratingType === 'from_traveler',
                        );
                        return (
                          <>
                            <Typography variant="body1">
                              {`${travelerRating?.rating ?? 0}⭐`}
                            </Typography>
                            {travelerRating?.review?.trim() && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}
                              >
                                "{travelerRating.review}"
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  }
                />*/}
              </Box>
            </Box>
          )}

          {/* Traveller Details */}
          {activeTab === 2 && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#00c996',
                }}
              >
                Traveller Details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr',
                  },
                  gap: 3,
                }}
              >
                <GridItem
                  label="User Id"
                  value={booking?.travelerDetails?.userId || 'N/A'}
                />
                <GridItem
                  label="Traveller Name"
                  value={booking?.travelerDetails?.name || 'N/A'}
                />
                <GridItem
                  label="Phone Number"
                  value={booking?.travelerDetails?.phone || 'N/A'}
                />
                <GridItem
                  label="Email"
                  value={booking?.travelerDetails?.email || 'N/A'}
                />
                <GridItem
                  label="Vehicle Type"
                  value={booking?.travelerDetails?.vehicleType || 'N/A'}
                />
                <GridItem
                  label="Source"
                  value={booking?.travelerDetails?.source || 'N/A'}
                />
                <GridItem
                  label="Destination"
                  value={booking?.travelerDetails?.destination || 'N/A'}
                />
                <GridItem
                  label="Start Date"
                  value={booking?.travelerDetails?.startDate ? formatToIST(booking?.travelerDetails?.startDate) : 'N/A'}
                />
                <GridItem
                  label="End Date"
                  value={booking?.travelerDetails?.endDate ? formatToIST(booking?.travelerDetails?.endDate) : 'N/A'}
                />
               {/* <GridItem
                  label="Rating"
                  value={
                    <Box>
                      {(() => {
                        const senderRating = booking?.ratings?.find(
                          (r) => r.ratingType === 'from_shipment_owner',
                        );
                        return (
                          <>
                            <Typography variant="body1">
                              {`${senderRating?.rating ?? 0}⭐`}
                            </Typography>
                            {senderRating?.review?.trim() && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}
                              >
                                "{senderRating.review}"
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  }
                />*/}
{booking?.status === 'Delivered' && (
                  <>
                    <GridItem
                      label="Rating"
                      value={
                        booking?.travelerRating?.rating != null && Number(booking.travelerRating.rating) > 0
                          ? `${booking.travelerRating.rating}⭐`
                          : 'Not Rated'
                      }
                    />
                    <GridItem
                      label="Review"
                      value={
                        booking?.travelerRating?.review && String(booking.travelerRating.review).trim()
                          ? booking.travelerRating.review
                          : 'Not Rated'
                      }
                    />
                  </>
                )}


                {/*
                <GridItem
                  label="Aadhar Number"
                  value={booking.travelerDetails?.aadharNumber}
                />
                <GridItem
                  label="License Number"
                  value={booking.travelerDetails?.drivingLicense}
                />
                */}
              </Box>
            </Box>
          )}

          {/* Tracking */}
          {activeTab === 3 && (
            <Box>
              <TimeLine
                data={booking.timeline || []}
                status={booking.status}
              />
            </Box>
          )}

          {/* Fare Details */}
          {activeTab === 4 && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#00c996',
                }}
              >
                Fare Breakdown
              </Typography>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Price Details
                  </Typography>

                  {/* Horizontal fare breakdown */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Typography variant="body1">Base Fare</Typography>
                      <Typography variant="body1">
                        ₹{booking.fareDetails?.originalFare?.toFixed(2) || 0}
                      </Typography>
                    </Box>

                    {/* <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Typography variant="body1">Distance Charge</Typography>
                      <Typography variant="body1">
                        ₹{booking.fareDetails?.distanceCharge || 0}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Typography variant="body1">Weight Charge</Typography>
                      <Typography variant="body1">
                        ₹{booking.fareDetails?.weightCharge || 0}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Typography variant="body1">Priority Delivery</Typography>
                      <Typography variant="body1">
                        ₹{booking.fareDetails?.priorityCharge || 0}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Typography variant="body1">Service Fee</Typography>
                      <Typography variant="body1">
                        ₹{booking.fareDetails?.serviceFee || 0}
                      </Typography>
                    </Box>*/}

                    {booking.fareDetails?.discount > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        { /* <Typography
                          variant="body1"
                          sx={{ color: 'success.main' }}
                        >
                          Discount Applied
                          {booking.offerCode && (
                            <Chip
                              label={booking.offerCode}
                              size="small"
                              sx={{ ml: 1, height: 20 }}
                              color="success"
                            />
                          )}
                        </Typography>*/}
                        <Typography
                          variant="body1"
                          sx={{ color: 'success.main' }}
                        >
                          -₹{booking.fareDetails?.discount || 0}
                        </Typography>
                      </Box>
                    )}
                    {/*  <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #f0f0f0',
                        bgcolor: '#f0f0f0',
                        px: 1,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Original Fare
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ₹{booking.fareDetails?.originalFare?.toFixed(2) || 0}
                      </Typography>
                    </Box>*/}
                  </Box>

                  {/* Taxes */}
                  <Box
                    sx={{
                      backgroundColor: '#f9f9f9',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 'bold' }}
                    >
                      Taxes and Fees
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2">SGST (9%)</Typography>
                      <Typography variant="body2">
                        ₹{booking.fareDetails?.sgst?.toFixed(2) || 0}
                        {/*  ₹{booking.fareDetails?.sgst || 0}*/}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2">CGST (9%)</Typography>
                      <Typography variant="body2">
                        ₹{booking.fareDetails?.cgst?.toFixed(2) || 0}
                        {/* ₹{booking.fareDetails?.cgst || 0}*/}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        Transaction Charge
                      </Typography>
                      <Typography variant="body2">
                        ₹
                        {booking.fareDetails?.transactionCharge?.toFixed(2) ||
                          0}
                      </Typography>
                    </Box>

                    {booking.fareDetails?.commission > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.5,
                        }}
                      >
                        <Typography variant="body2">Platform fee</Typography>
                        <Typography variant="body2">
                          ₹{booking.fareDetails?.commission?.toFixed(2) || 0}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Total */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 2,
                      borderTop: '1px solid #e0e0e0',
                      borderBottom: '1px solid #e0e0e0',
                      mt: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      ₹{booking.fareDetails?.totalFare || 0}
                    </Typography>
                  </Box>

                  {/* Promo Discount Details */}
                  {booking.fareDetails?.promoDiscountAmount > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        py: 2,
                        mt: 2,
                        borderTop: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        bgcolor: '#f0f9ff',
                        px: 2,
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ color: 'success.main', fontWeight: 500 }}
                        >
                          Promo Discount
                          {booking.fareDetails?.promoCode && (
                            <Chip
                              label={booking.fareDetails.promoCode}
                              size="small"
                              sx={{ ml: 1, height: 20 }}
                              color="success"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: 'success.main', fontWeight: 500 }}
                        >
                          -₹{booking.fareDetails?.promoDiscountAmount?.toFixed(2) || 0}
                        </Typography>
                      </Box>
                      {booking.fareDetails?.appliedPromoId && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                        >
                          Promo ID: {booking.fareDetails.appliedPromoId}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Payment method */}
                  {/*   <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    <Typography variant="body2">Payment Method</Typography>
                    <Chip
                      label={booking.paymentMethod || "Cash on Delivery"}
                      color={
                        booking.paymentMethod === "Online"
                          ? "primary"
                          : "default"
                      }
                      size="small"
                    />
                  </Box>*/}
                </CardContent>
              </Card>

              {/* Payout Details for Delivered Status */}
              {booking.status === 'Delivered' && (
                <Card variant="outlined" sx={{ mb: 3, bgcolor: '#e8f5e8' }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 'bold', color: 'success.main' }}
                    >
                      Payout Details
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body1">Traveler Payout</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          ₹{booking?.payoutDetails?.amount?.toFixed(2) || 0}
                          {/* ₹
                          {Math.round(
                            (booking.fareDetails?.totalFare || 0) * 0.75,
                          )}*/}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        {/* <Typography variant="body1">Platform fee</Typography> */}
                        {/* <Typography variant="body1">
                          ₹{booking.fareDetails?.commission?.toFixed(2) || 0}
                          {/* ₹
                          {Math.round(
                            (booking.fareDetails?.totalFare || 0) * 0.25,
                          )}*/}
                        {/* </Typography>*/}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderTop: '1px solid #c8e6c9',
                        }}
                      >
                        <Typography variant="body2">Payout Status</Typography>
                        <Chip
                          label={booking?.payoutDetails?.status || 'PENDING'}
                          color={
                            booking?.payoutDetails?.status === 'RECEIVED'
                              ? 'success'
                              : 'warning'
                          }
                          size="small"
                        />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body2">Transaction ID</Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {booking?.payoutDetails?.transactionId ||
                              generateTransactionId()}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              copyToClipboard(
                                booking?.payoutDetails?.transactionId ||
                                  generateTransactionId(),
                                'Transaction ID',
                              )
                            }
                            sx={{ p: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Refund Details for Cancelled Status */}
              {booking.status === 'Canceled' && (
                <Card variant="outlined" sx={{ mb: 3, bgcolor: '#ffebee' }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 'bold', color: 'error.main' }}
                    >
                      Refund Details
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body1">Original Amount</Typography>
                        <Typography variant="body1">
                          ₹{booking.fareDetails?.totalFare || 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body1">
                          Cancellation Fee
                        </Typography>
                        <Typography variant="body1">
                          ₹
                          {Math.round(
                            (booking.fareDetails?.totalFare || 0) * 0.1,
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderTop: '1px solid #ffcdd2',
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          Refund Amount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          ₹
                          {Math.round(
                            (booking.fareDetails?.totalFare || 0) * 0.9,
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body2">Refund Status</Typography>
                        <Chip label="Processed" color="warning" size="small" />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body2">Refund Method</Typography>
                        <Typography variant="body2">
                          {booking.paymentMethod === 'Online'
                            ? 'Original Payment Method'
                            : 'Bank Transfer'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                        }}
                      >
                        <Typography variant="body2">Transaction ID</Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {generateTransactionId()}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              copyToClipboard(
                                generateTransactionId(),
                                'Transaction ID',
                              )
                            }
                            sx={{ p: 0.5 }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Additional fare information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Fare Information
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Base fare includes first 5km of travel
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Distance charge applies ₹15 per additional km
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Weight charge applies ₹10 per kg over 5kg
                  </Typography>
                  <Typography variant="body2">
                    • Priority delivery ensures 50% faster delivery time
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

/* --- Helper Components --- */

const GridItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        color: '#666',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body1">{value || '-'}</Typography>
  </Box>
);

export default BookingDetails;
